"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, MapPin, Clock, CircleCheck as CheckCircle2, Loader, ArrowRight, Calendar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const contactDetails = [{
  icon: Mail,
  label: "Email Address",
  value: "info@djtdconsulting.com",
  href: "mailto:info@djtdconsulting.com",
  subtext: "Prompt responses within one business day"
}, {
  icon: MapPin,
  label: "Office & Service Area",
  value: "San Diego, California",
  href: "",
  subtext: "Serving individuals and families nationwide"
}];
const topicOptions = [{
  value: "life-insurance",
  label: "Life Insurance (Term & Permanent)"
}, {
  value: "annuities-myga",
  label: "Fixed & Fixed Indexed Annuities (MYGA)"
}, {
  value: "retirement-income",
  label: "Retirement Income Strategies"
}, {
  value: "final-expense",
  label: "Final Expense Insurance"
}, {
  value: "income-protection",
  label: "Income Protection"
}, {
  value: "legacy-planning",
  label: "Legacy Planning"
}, {
  value: "general",
  label: "General Financial Education & Questions"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center p-8 rounded-xl bg-card border border-border">
        <div className="size-14 rounded-full bg-muted flex items-center justify-center mb-4 text-primary">
          <CheckCircle2 className="size-7" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground mb-2">
          Message received
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6 leading-relaxed">
          {successMessage || "Thank you for reaching out. An independent advisor will review your note and respond within one business day."}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-muted">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Name</FormLabel>
            <FormControl>
              <Input placeholder="Your full name" className="bg-card text-foreground border-input placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" className="bg-card text-foreground border-input placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">
                Phone <span className="text-muted-foreground font-normal">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Your phone number" className="bg-card text-foreground border-input placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="topic" rules={{
      required: "Please select a topic of interest"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Topic of Interest</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-full bg-card text-foreground border-input focus-visible:ring-ring">
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover text-popover-foreground border-border">
                {topicOptions.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i}>
                    {opt.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide details on how we can assist you",
      minLength: {
        value: 10,
        message: "Please enter at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help?" rows={4} className="bg-card text-foreground border-input placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 font-semibold shadow-sm transition-[color,background-color,border-color,box-shadow]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending message...
          </> : <>
            <span>Send Message</span>
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>

      <p className="text-center text-xs text-muted-foreground pt-1">
        We respect your privacy. No pressure, no obligations, and no spam.
      </p>
    </Form>;
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="contact" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative border-t border-border">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Advisory Details */}
          <motion.div variants={itemVariants} className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="bg-muted text-foreground border-border uppercase tracking-widest text-[11px] font-semibold px-3 py-1 rounded-full">
                Get in touch
              </Badge>
              <h2 className="font-default text-3xl sm:text-4xl lg:text-[2.6rem] font-bold text-foreground leading-[1.2] tracking-tight">
                Speak with an independent insurance professional.
              </h2>
              <div className="flex items-center gap-2 pt-1 pb-1">
                <span className="size-2 rounded-full bg-primary" />
                <span className="h-px w-16 bg-border" />
              </div>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
                Ask a question or request a clear walkthrough of your life insurance, annuity, or retirement income options. We prioritize education, clarity, and patient guidance over high-pressure sales.
              </p>
            </div>

            {/* Information Guidance Cards */}
            <div className="space-y-3 pt-2">
              {contactDetails.map((item, i) => {
              const IconComponent = item.icon;
              return <Card key={item.label} data-index={i} className="border border-border/80 bg-card rounded-xl shadow-xs py-4 px-5 transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                    <CardContent className="p-0 flex items-start gap-4">
                      <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0 text-primary mt-0.5">
                        <IconComponent className="size-5" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {item.label}
                        </p>
                        {item.href ? <Link href={item.href} className="text-base font-semibold text-foreground hover:underline block leading-snug">
                            {item.value}
                          </Link> : <p className="text-base font-semibold text-foreground leading-snug">
                            {item.value}
                          </p>}
                        <p className="text-xs text-muted-foreground">
                          {item.subtext}
                        </p>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Office Hours Callout */}
            <div className="p-4 rounded-lg bg-muted/60 border border-border flex items-center gap-3">
              <Clock className="size-5 text-muted-foreground shrink-0" />
              <div className="text-sm">
                <span className="font-semibold text-foreground">Consulting Hours:</span>{" "}
                <span className="text-muted-foreground">Monday–Thursday, 10 AM to 2 PM (PST)</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Consultation Request Form Plinth */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm relative">
              <div className="mb-6 border-b border-border pb-5">
                <h3 className="font-default text-xl sm:text-2xl font-bold text-foreground">
                  Send a consultation request
                </h3>
                <p className="text-muted-foreground text-sm mt-1 mb-4">
                  Fill out the form below and an independent specialist will be in touch.
                </p>
                <Button asChild variant="outline" className="border-border text-foreground hover:bg-muted font-medium shadow-xs">
                  <Link to="https://calendly.com/concierge-altavidaoffices" target="_blank" rel="noopener noreferrer">
                    <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                    Schedule a Call
                  </Link>
                </Button>
              </div>

              <FormProvider formId="contact_form" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              topic: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false
              },
              topic: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Request consultation" successMessage="Thank you for contacting DJTD Consulting. We will review your note and respond shortly.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}